import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Share2, Copy, Users, Coins, TrendingUp, Megaphone, Sparkles, DollarSign,
  Rocket, ArrowRight, Link2, Handshake, Loader2, Check, Radio, Smartphone, Trophy, MessageCircle,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const PARTNERS = [
  { icon: Handshake, title: "Partners", desc: "Agencies, communities and B2B partners embedding My Kind in their offer." },
  { icon: Radio, title: "Influencers", desc: "Creators who share their affiliate link with their audience." },
  { icon: Smartphone, title: "Apps", desc: "Products that refer users to My Kind and earn on every signup." },
];

export default function Affiliate() {
  const { user, fmtMoney } = useApp();
  const [s, setS] = useState(null);
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    api.get("/affiliate/summary").then(({ data }) => setS(data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
    api.get("/affiliate/leaderboard").then(({ data }) => setLb(data)).catch(() => {});
  }, [user]);

  const link = s ? `${window.location.origin}${s.link_path}` : "";
  const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); toast.success(label); } catch { toast.error("Copy failed"); } };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Build with My Kind — God Mode PaaS", url: link }); return; } catch { /* fallthrough */ } }
    copy(link, "Affiliate link copied");
  };
  const banner = `🚀 I build with My Kind — the self-evolving God Mode platform. Join free and get started: ${link}`;
  const waMsg = `🙏 Join me on My Kind — the sovereign, ad-free, AI-native platform where you own your data & earn from it. Sign up free (takes a minute): ${link}`;
  const inviteWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(waMsg)}`, "_blank", "noopener,noreferrer");

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Handshake size={14} /> Affiliate Partner Program</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Promote My Kind. Earn on every signup.</h1>
        <p className="text-sec mt-2 text-sm max-w-2xl">Partners, influencers and apps share their unique My Kind link and earn a <b className="text-brand">{s?.signup_bonus_credits ?? 25}-credit bonus for every user who registers</b> — plus lifetime 3-tier cash on everything they buy.</p>
      </motion.div>

      {/* How it works */}
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {[
          { icon: Link2, t: "1 · Share your link", d: "Grab your unique affiliate link and drop it anywhere — bio, video, newsletter, app." },
          { icon: Users, t: "2 · They register", d: `Each new user who signs up through your link earns you ${s?.signup_bonus_credits ?? 25} credits instantly.` },
          { icon: DollarSign, t: "3 · Earn for life", d: "Then earn 9% / 6% / 3% cash on every credit purchase across three tiers of your network." },
        ].map((c) => (
          <div key={c.t} className="rud-card rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><c.icon size={18} /></div>
              <div className="font-medium text-sm">{c.t}</div>
            </div>
            <p className="text-xs text-sec mt-2 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>

      {/* Who it's for */}
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {PARTNERS.map((p) => (
          <div key={p.title} className="rounded-2xl border border-rud bg-elevated p-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-brand/15 grid place-items-center text-brand shrink-0"><p.icon size={16} /></div>
              <div className="font-medium text-sm">{p.title}</div>
            </div>
            <p className="text-xs text-sec mt-2">{p.desc}</p>
          </div>
        ))}
      </div>

      {/* Dashboard / CTA */}
      {loading ? (
        <div className="flex items-center gap-2 text-sec mt-10"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : !user ? (
        <div className="rud-card rounded-2xl p-8 mt-8 text-center" data-testid="affiliate-join">
          <Sparkles size={26} className="mx-auto text-brand mb-3" />
          <div className="font-serif text-2xl tracking-tight">Join the program — it's free</div>
          <p className="text-sec text-sm mt-1">Create your account to unlock your affiliate link and dashboard.</p>
          <Link to="/auth" data-testid="affiliate-join-cta" className="btn-primary rounded-full h-11 px-6 text-sm inline-flex items-center gap-2 mt-4"><Rocket size={16} /> Become an affiliate <ArrowRight size={15} /></Link>
        </div>
      ) : (
        <div className="mt-8" data-testid="affiliate-dashboard">
          {/* Link */}
          <div className="rud-card rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Link2 size={14} /> Your affiliate link</div>
            <div className="flex items-center gap-2 bg-elevated rounded-lg p-3 font-mono text-xs break-all border border-rud">
              <span data-testid="affiliate-link" className="flex-1">{link}</span>
              <button data-testid="affiliate-copy" onClick={() => copy(link, "Affiliate link copied")} className="text-brand shrink-0"><Copy size={15} /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid="affiliate-share" onClick={share} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Share2 size={14} /> Share link</button>
              <button onClick={() => copy(s.code, "Code copied")} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5">Code: <b className="text-brand">{s.code}</b></button>
              <button onClick={() => copy(banner, "Promo message copied")} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Megaphone size={14} /> Copy promo message</button>
              <button data-testid="affiliate-whatsapp" onClick={inviteWhatsApp} className="rounded-full h-9 px-4 text-sm flex items-center gap-1.5 text-white" style={{ background: "#25D366" }}><MessageCircle size={14} /> Invite 5 friends on WhatsApp</button>
            </div>
          </div>

          {/* Top sharers leaderboard */}
          {lb && lb.top && lb.top.length > 0 && (
            <div className="rud-card rounded-2xl p-6 mt-4" data-testid="affiliate-leaderboard">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Trophy size={14} /> Top sharers</div>
              <p className="text-sec text-[11px] mb-3">The members spreading My Kind the most. Invite friends to climb the board.</p>
              <div className="space-y-2">
                {lb.top.map((r) => (
                  <div key={r.rank} data-testid={`lb-${r.rank}`}
                    className={`flex items-center gap-3 rounded-xl p-3 border ${r.you ? "border-brand bg-brand/5" : "border-rud bg-elevated"}`}>
                    <span className="w-7 text-center font-serif text-lg shrink-0">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : r.rank}</span>
                    <span className="flex-1 truncate text-sm font-medium">{r.name}{r.country ? ` · ${r.country}` : ""}</span>
                    <span className="text-sm text-brand font-semibold tabular-nums">{r.signups}</span>
                    <span className="text-[11px] text-sec">joins</span>
                  </div>
                ))}
              </div>
              {lb.me && !lb.me.in_top && lb.me.signups > 0 && (
                <p className="text-[11px] text-sec mt-3">Your rank: <b className="text-brand">#{lb.me.rank || "—"}</b> · {lb.me.signups} joins — keep sharing to climb!</p>
              )}
              {lb.me && lb.me.signups === 0 && (
                <p className="text-[11px] text-sec mt-3">Share your link to get on the board — earn {lb.signup_bonus_credits} credits for every signup!</p>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Stat icon={Radio} label="Link clicks" value={s.clicks} testid="affiliate-clicks" />
            <Stat icon={Users} label="Signups" value={s.signups} sub={`${s.conversion_pct}% conversion`} testid="affiliate-signups" />
            <Stat icon={Coins} label="Bonus credits earned" value={s.credits_earned} sub={`${s.signup_bonus_credits}/signup`} testid="affiliate-credits" />
            <Stat icon={DollarSign} label="Cash earned" value={fmtMoney(s.cash_earned)} sub={`Cash out at ${fmtMoney(s.cashout_threshold)}`} testid="affiliate-cash" />
          </div>

          {/* Tiers */}
          <div className="rud-card rounded-2xl p-6 mt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><TrendingUp size={14} /> Lifetime 3-tier commission</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {(s.tiers || []).map((t) => (
                <div key={t.tier} className="rounded-xl border border-rud bg-elevated p-4">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">Tier {t.tier}</span><span className="text-brand font-semibold">{t.rate}%</span></div>
                  <div className="text-[11px] text-sec mt-1">of every credit purchase</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-sec mt-3 flex items-center gap-1.5"><Check size={13} className="text-brand" /> Paid instantly into your Prosperity-Mint Money balance.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rud-card rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-brand shrink-0" />
        <div className="text-[11px] uppercase tracking-widest text-sec">{label}</div>
      </div>
      <div className="font-serif text-3xl mt-1.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-sec mt-0.5">{sub}</div>}
    </div>
  );
}
